import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

export type WaitLoaderProps = {
  /** Primary line (trailing ellipsis/dots are added). */
  title?: string;
  /** Secondary reassurance line under the bar when no step list is shown. */
  secondary?: string;
  /** Ordered pipeline steps; highlights current and marks earlier steps complete. */
  steps?: readonly string[];
  currentStepIndex?: number;
  className?: string;
};

function useAnimatedDots(active: boolean) {
  const [periodCount, setPeriodCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setPeriodCount(0);
      return;
    }
    setPeriodCount(1);
    const periodInterval = setInterval(() => {
      setPeriodCount((prev) => (prev % 3) + 1);
    }, 300);
    return () => clearInterval(periodInterval);
  }, [active]);

  return periodCount > 0 ? '.'.repeat(periodCount) : '';
}

function WaitLoaderStepList({
  steps,
  currentStepIndex
}: {
  steps: readonly string[];
  currentStepIndex: number;
}) {
  return (
    <ul className="mt-4 space-y-2 text-left" aria-label="Unit creation progress">
      {steps.map((step, index) => {
        const isComplete = index < currentStepIndex;
        const isCurrent = index === currentStepIndex;
        const statusLabel = isComplete ? 'Complete' : isCurrent ? 'In progress' : 'Pending';

        return (
          <li
            key={step}
            className={`flex items-start gap-2 text-sm leading-snug ${
              isCurrent ? 'font-medium text-gray-900' : isComplete ? 'text-gray-500' : 'text-gray-400'
            }`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
              {isComplete ? (
                <Check className="h-4 w-4 text-confirm-action" strokeWidth={2.5} />
              ) : isCurrent ? (
                <Loader2 className="h-4 w-4 animate-spin text-action-blue" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-gray-300" />
              )}
            </span>
            <span>
              <span className="sr-only">{statusLabel}: </span>
              {step}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Shared wait panel: title + animated dots + indeterminate bar + secondary line or step list.
 */
export function WaitLoader({
  title = 'Please wait',
  secondary = 'Please wait.',
  steps,
  currentStepIndex = 0,
  className = ''
}: WaitLoaderProps) {
  const baseTitle = title.replace(/\.+$/, '');
  const dots = useAnimatedDots(true);
  const showStepList = steps != null && steps.length > 0;

  return (
    <div className={`flex flex-col items-center justify-center px-4 py-6 text-center ${className}`}>
      <div className="w-full max-w-sm rounded-lg border border-action-blue/20 bg-gradient-to-b from-action-blue/10 to-white px-6 py-8 shadow-sm">
        <p className="text-base font-medium text-gray-800">
          {baseTitle}
          <span className="inline-block w-[1.25em] text-left tabular-nums text-action-blue" aria-hidden>
            {dots}
          </span>
        </p>

        <div
          className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-action-blue/15"
          role="progressbar"
          aria-valuetext={showStepList ? steps[currentStepIndex] ?? baseTitle : baseTitle}
          aria-busy="true"
        >
          <div className="wait-loader-bar h-full w-1/3 rounded-full bg-action-blue" />
        </div>

        {showStepList ? (
          <WaitLoaderStepList steps={steps} currentStepIndex={currentStepIndex} />
        ) : (
          <p className="mt-4 text-sm leading-relaxed text-gray-600">{secondary}</p>
        )}
      </div>

      <style>{`
        @keyframes wait-loader-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
        .wait-loader-bar {
          animation: wait-loader-bar 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
